import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { htmlDecodeBody } from 'express-sanitize-escape';
import styled from 'styled-components';

import SectionNavLink from '@zg-rentals/multibrand-ui/lib/SectionNavLinkCometTemp';
import { Paragraph, spaceMixin, Text, token } from '@zillow/constellation';

import { getApplicationStatus, getIsCompletedOrInProgress } from './ListingApplicationCardUtil';

import './ListingApplicationCard.scss';
import { getLandlordReviewPath } from '../../../../../../../constants/urls';
import { normalizeApplication } from '../../../../../../../utils/listingApplicationListUtil';
import UnstyledLink from '../../../../../../../components/UnstyledLink';
import { uiApplicationStatusTypes } from '../../../../../../../constants/statusConstants';
import ListingApplicationCardStatusHeader from './ListingApplicationCardStatusHeader';
import { getTitleCase } from '../../../../../../../utils/formatLogicUtil';
import useQueryVariables from '../../../../../../../hooks/useQueryVariables';
import { FULLSTORY_EXCLUDE } from '../../../../../../../constants/fullstory';
import { useListingUser } from '../../../../../../../hooks/useListingUser';
import { emitApplicationClickEvent } from '../../../../../../../../client/analytics/clickstreamEvents';

const ListingApplicationCardTitle = styled.div`
  display: flex;
  align-items: center;
  padding: ${spaceMixin(0.5)} 0;
`;

const ApplicationWrapper = styled.div`
  ${({ isNewApplication }) =>
    isNewApplication &&
    `
        .ListingApplicationCard__Text {
            font-weight: bold;
        }
    `}
`;

export const NotificationIcon = styled.i`
  width: 10px;
  height: 10px;
  margin-right: 6px;
  border-radius: 50%;
  background-color: ${token('colors.brand')};
`;

export default function ListingApplicationCard({ application }) {
  const { user, listing } = useListingUser();
  const { propertyId } = useQueryVariables();
  const { applicants, applicationIsViewed, applicationId } = application;
  const { type } = normalizeApplication(application);

  const timeoutRef = useRef();
  const [isDisabledDuringTimeout, setIsDisabledDuringTimeout] = useState(false);

  const isCompletedOrInProgress = getIsCompletedOrInProgress(type);
  const isInvited = type === uiApplicationStatusTypes.INVITED.id;
  const names = htmlDecodeBody(applicants.map((applicant) => getTitleCase(applicant.name)).join(', '));
  const status = getApplicationStatus({ application, type });
  const customClass = isCompletedOrInProgress && !isDisabledDuringTimeout ? '' : 'ListingApplicationCard__Disabled';

  function customClickHandler() {
    if (!isDisabledDuringTimeout) {
      emitApplicationClickEvent(listing, user);
    }
  }

  function onLinkClick() {
    /**
     * Prevent the user from clicking to view the application in multiple
     * new tabs.
     */
    setIsDisabledDuringTimeout(true);
    // Setting to ref for correct context when executed.
    timeoutRef.current = setTimeout(
      () => setIsDisabledDuringTimeout(false),
      5000, // 5 seconds.
    );
  }

  useEffect(() => {
    /**
     * If the user clicks to view in the same tab, then the component
     * unmounts and the timeout is cleared immediately.
     */
    return () => clearTimeout(timeoutRef.current);
  }, []);

  // must check `isCompletedOrInProgress` because we don't want "inactive" applications to show up as unread/new
  const isNewApplication = isCompletedOrInProgress && !applicationIsViewed && !isInvited;

  const sectionNavLink = (
    <SectionNavLink
      description={
        <ApplicationWrapper
          id={`application-card-${applicationId}`}
          isNewApplication={isNewApplication}
          aria-label={isNewApplication ? 'Unread application' : null}
        >
          {isCompletedOrInProgress && (
            <ListingApplicationCardStatusHeader
              {...{
                applicants,
                applicationId,
              }}
            />
          )}
          <ListingApplicationCardTitle data-test="ListingApplicationCard__Title">
            {isNewApplication && <NotificationIcon />}
            <Text className={`ListingApplicationCard__Text ${FULLSTORY_EXCLUDE}`} marginRight="xs" type="h4">
              {names}
            </Text>
          </ListingApplicationCardTitle>
          <Paragraph fontType="smallBody" fontColor="textMedium" className="ListingApplicationCard__Text">
            {status}
          </Paragraph>
          {applicants.length === 1 && isInvited ? (
            <Paragraph fontColor="textMedium" classname={FULLSTORY_EXCLUDE} fontType="smallBody">
              {applicants[0].email}
            </Paragraph>
          ) : null}
        </ApplicationWrapper>
      }
      customClass={customClass}
      customClickHandler={customClickHandler}
    />
  );

  return isCompletedOrInProgress && !isDisabledDuringTimeout ? (
    <UnstyledLink
      id={`link-to-application-${applicationId}`}
      to={getLandlordReviewPath(propertyId, applicationId)}
      onClick={onLinkClick}
      aria-label="View application"
    >
      {sectionNavLink}
    </UnstyledLink>
  ) : (
    sectionNavLink
  );
}

ListingApplicationCard.propTypes = {
  application: PropTypes.shape({
    applicationId: PropTypes.string.isRequired,
    applicants: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string.isRequired,
      }),
    ),
    applicationIsViewed: PropTypes.bool.isRequired,
  }).isRequired,
};
